import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPaperPlane,
  faArrowLeft,
  faArrowRight,
  faMagnifyingGlass,
  faXmark,
  faCheck,
  faDesktop,
  faMobileScreen,
  faUserGraduate,
  faUserClock,
  faUsers,
  faUserCheck,
  faBuilding,
  faFolderOpen,
  faFloppyDisk,
  faPen,
  faTrashCan,
  faEnvelopeOpenText,
  faTriangleExclamation,
  faCircleCheck,
  faRotateRight,
  faEye,
  faFlask,
} from '@fortawesome/free-solid-svg-icons';
import emailTemplates from './emailTemplates';
import { renderEmail, fillTags, firstNameOf, leftoverPlaceholders, BUTTONS } from './emailDesign';
import { fromServer, DISPLAY_TZ } from './Servertime';
import './Sendemailslegacy.css'; // LEGACY: old global classes other pages may rely on
import './SendEmails.css';

/* =========================================================
   Admin › Emails
   - Sent: every email campaign with its report (sent / failed, who got it)
   - New email: 1) who gets it  2) write it (with live preview)  3) test & send
   - Centers: the list of centers and schools
   Same endpoints as before. Emails go out in batches of 10, like before.
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = (extra = {}) => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}`, ...extra } });

const CHUNK_SIZE = 10;
const CHUNK_BYTES = 80000; // the server refuses a request over 100 KB: long emails go in smaller batches
const CHUNK_DELAY = 5000;
const ERROR_DELAY = 10000;
const TEST_KEY = 'rm-test-email';
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const packBatches = (list, extra) => {
  const base = JSON.stringify(extra).length;
  const out = [];
  let cur = [];
  let size = base;
  list.forEach((p) => {
    const n = JSON.stringify(p).length + 1;
    if (cur.length && (cur.length >= CHUNK_SIZE || size + n > CHUNK_BYTES)) {
      out.push(cur);
      cur = [];
      size = base;
    }
    cur.push(p);
    size += n;
  });
  if (cur.length) out.push(cur);
  return out;
};

const SEGMENTS = [
  { key: 'demo-done', icon: faUserCheck, label: 'Tried a free class', desc: 'Attended the free class', query: 'studentType=demo&filterType=completed&enrollmentFilter=all' },
  { key: 'demo-missed', icon: faUserClock, label: "Missed the free class", desc: 'Booked a free class but did not attend', query: 'studentType=demo&filterType=not-completed&enrollmentFilter=all' },
  { key: 'active', icon: faUserGraduate, label: 'Current students', desc: 'Paid, with a class in the last 2 months', query: 'studentType=active' },
  { key: 'former', icon: faUsers, label: 'Former students', desc: 'Paid before, no class in 2+ months', query: 'studentType=enrolled&filterType=all&enrollmentFilter=all' },
  { key: 'centers', icon: faBuilding, label: 'Centers & schools', desc: 'From your centers list' },
  { key: 'saved', icon: faFolderOpen, label: 'A saved list', desc: 'A list you saved before' },
];
const SEG = Object.fromEntries(SEGMENTS.map((s) => [s.key, s]));

const TAGS = {
  student: [
    ['{{firstName}}', "Child's first name"],
    ['{{studentName}}', 'Full name'],
    ['{{lastClassDate}}', 'Last class date'],
  ],
  center: [['{{centerName}}', 'Center name']],
};

const EMPTY_DRAFT = { templateKey: '', subject: '', preheader: '', heading: '', message: '', buttonType: 'whatsapp', buttonLabel: '', buttonUrl: '' };
const EMPTY_CENTER = { centerName: '', email: '', mobile: '', address: '' };

/* class dates are Lebanon wall-clock digits: read them as they are */
const classDate = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' }) : '';
};
const sentAt = (v) => {
  const d = fromServer(v);
  return d ? d.toLocaleString('en-US', { timeZone: DISPLAY_TZ, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
};
// show {{tags}} in readable words in the lists
const readable = (t) =>
  String(t || '')
    .replace(/\{\{firstName\}\}/g, "(child's name)")
    .replace(/\{\{studentName\}\}/g, "(child's full name)")
    .replace(/\{\{lastClassDate\}\}/g, '(last class date)')
    .replace(/\{\{centerName\}\}/g, '(center name)');
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const isEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(e || '').trim());

const toStudent = (s) => ({ id: `s-${s.id}`, rawId: s.id, kind: 'student', name: s.studentName || 'Student', email: s.email, lastClassDate: s.lastClassDate });
const toCenter = (c) => ({ id: `c-${c.Id}`, rawId: c.Id, kind: 'center', name: c.CenterName, email: c.Email, mobile: c.Mobile, address: c.Address });

/* one person's values for the tags */
const valuesFor = (r) =>
  r.kind === 'center'
    ? { centerName: r.name }
    : { firstName: firstNameOf(r.name), studentName: r.name, lastClassDate: classDate(r.lastClassDate) };

/* old campaigns were saved as one row per batch of 10: join them back together */
const groupLogs = (logs) => {
  const out = [];
  logs
    .filter((l) => l.Type !== 'test')
    .forEach((l) => {
      const prev = out[out.length - 1];
      const t = fromServer(l.SentAt)?.getTime() || 0;
      if (prev && prev.subject === l.Subject && prev.type === l.Type && Math.abs(prev.lastTime - t) < 60 * 60000) {
        prev.ids.push(l.Id);
        prev.total += Number(l.RecipientCount || 0);
        prev.sent += Number(l.SuccessCount || 0);
        prev.failed += Number(l.FailedCount || 0);
        prev.lastTime = t;
        return;
      }
      out.push({
        key: l.Id,
        ids: [l.Id],
        subject: l.Subject,
        type: l.Type,
        at: l.SentAt,
        lastTime: t,
        total: Number(l.RecipientCount || 0),
        sent: Number(l.SuccessCount || 0),
        failed: Number(l.FailedCount || 0),
      });
    });
  return out;
};

/* ---------- small pieces ---------- */
const Steps = ({ step, setStep, canGo }) => (
  <ol className="rmase-steps">
    {['Who gets it', 'Write', 'Test & send'].map((label, i) => {
      const n = i + 1;
      return (
        <li key={label} className={`${step === n ? 'is-on' : ''} ${step > n ? 'is-done' : ''}`}>
          <button type="button" onClick={() => canGo(n) && setStep(n)} disabled={!canGo(n)}>
            <span>{step > n ? <FontAwesomeIcon icon={faCheck} /> : n}</span>
            {label}
          </button>
        </li>
      );
    })}
  </ol>
);

/* computer view: the email at its real width (660px), shrunk to fit the panel */
const DESKTOP_W = 660;
const DESKTOP_H = 900;
const ScaledFrame = ({ html, device }) => {
  const box = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setScale(Math.min(1, entry.contentRect.width / DESKTOP_W)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const sandbox = 'allow-popups allow-popups-to-escape-sandbox';
  return (
    <div className={`rmase-frame is-${device}`} ref={box}>
      {device === 'desktop' ? (
        <div className="rmase-scaled" style={{ height: DESKTOP_H * scale }}>
          <iframe
            title="Email preview"
            srcDoc={html}
            sandbox={sandbox}
            style={{ width: DESKTOP_W, height: DESKTOP_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}
          />
        </div>
      ) : (
        <iframe title="Email preview" srcDoc={html} sandbox={sandbox} />
      )}
    </div>
  );
};

const Preview = ({ html, device, setDevice, subject, onClose }) => (
  <div className="rmase-preview">
    <div className="rmase-preview-bar">
      <div className="rmase-seg" role="radiogroup" aria-label="Preview size">
        <button type="button" role="radio" aria-checked={device === 'desktop'} className={device === 'desktop' ? 'is-on' : ''} onClick={() => setDevice('desktop')}>
          <FontAwesomeIcon icon={faDesktop} /> Computer
        </button>
        <button type="button" role="radio" aria-checked={device === 'mobile'} className={device === 'mobile' ? 'is-on' : ''} onClick={() => setDevice('mobile')}>
          <FontAwesomeIcon icon={faMobileScreen} /> Phone
        </button>
      </div>
      {onClose && (
        <button type="button" className="rmase-icon-btn" onClick={onClose} aria-label="Close preview">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
    </div>
    <div className="rmase-inbox">
      <span className="rmase-inbox-from">RoboMind Academy</span>
      <strong>{subject || 'No subject yet'}</strong>
    </div>
    <ScaledFrame html={html} device={device} />
  </div>
);

const SendEmails = ({ userData }) => {
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'new' ? 'new' : params.get('tab') === 'centers' ? 'centers' : 'sent';
  const go = (next) => {
    const p = new URLSearchParams(params);
    p.delete('view');
    p.delete('tab');
    if (next === 'new') p.set('view', 'new');
    if (next === 'centers') p.set('tab', 'centers');
    setParams(p, { replace: false });
  };

  const [toast, setToast] = useState(null);
  const say = useCallback((text, tone = 'good') => setToast({ text, tone, id: Date.now() }), []);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3800);
    return () => clearTimeout(t);
  }, [toast]);
  const [confirm, setConfirm] = useState(null);

  /* ================= Sent ================= */
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState('');
  const [report, setReport] = useState(null); // { campaign, loading, log, recipients, error }

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError('');
    try {
      const res = await axios.get(`${API}/api/admin/students/email-logs`, auth());
      setLogs(res.data?.logs || []);
    } catch {
      setLogsError("Couldn't load the sent emails.");
    } finally {
      setLogsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (view === 'sent') loadLogs();
  }, [view, loadLogs]);

  const campaigns = useMemo(() => groupLogs(logs), [logs]);
  const monthStats = useMemo(() => {
    const now = new Date();
    const inMonth = campaigns.filter((c) => {
      const d = fromServer(c.at);
      return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    const sent = inMonth.reduce((a, c) => a + c.sent, 0);
    const failed = inMonth.reduce((a, c) => a + c.failed, 0);
    return { count: inMonth.length, sent, failed, rate: sent + failed ? Math.round((sent / (sent + failed)) * 100) : null };
  }, [campaigns]);

  const openReport = async (c) => {
    setReport({ campaign: c, loading: true });
    try {
      const parts = await Promise.all(c.ids.map((id) => axios.get(`${API}/api/admin/students/email-logs/${id}`, auth())));
      const recipients = parts.flatMap((p) => p.data?.recipients || []);
      setReport({ campaign: c, loading: false, log: parts[0].data?.log, recipients });
    } catch {
      setReport({ campaign: c, loading: false, error: "Couldn't load this report." });
    }
  };

  /* ================= Centers ================= */
  const [centers, setCenters] = useState(null);
  const [centerModal, setCenterModal] = useState(null); // { mode, center }
  const [centerForm, setCenterForm] = useState(EMPTY_CENTER);
  const [centerSaving, setCenterSaving] = useState(false);
  const [centerError, setCenterError] = useState('');

  const loadCenters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/students/centers`, auth());
      setCenters(res.data?.centers || []);
    } catch {
      setCenters([]);
      say("Couldn't load the centers.", 'bad');
    }
  }, [say]);
  useEffect(() => {
    if (view === 'centers' && centers === null) loadCenters();
  }, [view, centers, loadCenters]);

  const openCenter = (center) => {
    setCenterError('');
    setCenterForm(center ? { centerName: center.CenterName || '', email: center.Email || '', mobile: center.Mobile || '', address: center.Address || '' } : EMPTY_CENTER);
    setCenterModal({ mode: center ? 'edit' : 'add', center });
  };
  const saveCenter = async (e) => {
    e.preventDefault();
    if (!centerForm.centerName.trim()) return setCenterError('Please enter the center name.');
    if (!isEmail(centerForm.email)) return setCenterError('Please enter a valid email.');
    setCenterSaving(true);
    setCenterError('');
    try {
      if (centerModal.mode === 'add') await axios.post(`${API}/api/admin/students/centers`, centerForm, auth());
      else await axios.put(`${API}/api/admin/students/centers/${centerModal.center.Id}`, centerForm, auth());
      setCenterModal(null);
      say(centerModal.mode === 'add' ? 'Center added' : 'Center updated');
      loadCenters();
    } catch (err) {
      setCenterError(err.response?.data?.error || "Couldn't save the center.");
    } finally {
      setCenterSaving(false);
    }
    return undefined;
  };
  const deleteCenter = (center) =>
    setConfirm({
      title: `Delete ${center.CenterName}?`,
      text: 'It will be removed from your centers list.',
      okLabel: 'Delete',
      danger: true,
      onOk: async () => {
        try {
          await axios.delete(`${API}/api/admin/students/centers/${center.Id}`, auth());
          say('Center deleted');
          loadCenters();
        } catch (err) {
          say(err.response?.data?.error || "Couldn't delete the center.", 'bad');
        }
      },
    });

  /* ================= New email ================= */
  const [step, setStep] = useState(1);
  const [segment, setSegment] = useState('');
  const [lists, setLists] = useState({}); // segment -> people (null while loading)
  const [groups, setGroups] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [device, setDevice] = useState('desktop');
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState(() => {
    try {
      return localStorage.getItem(TEST_KEY) || '';
    } catch {
      return '';
    }
  });
  const [testing, setTesting] = useState(false);
  const [progress, setProgress] = useState(null); // { total, done, sent, failed, finished }
  const [saveList, setSaveList] = useState(null); // { name, saving, error }
  const messageRef = useRef(null);
  const lastField = useRef('message');

  // counts for every audience as soon as the composer opens
  const loadSegment = useCallback(async (key) => {
    if (key === 'saved') return;
    setLists((l) => ({ ...l, [key]: null }));
    try {
      if (key === 'centers') {
        const res = await axios.get(`${API}/api/admin/students/centers`, auth());
        setLists((l) => ({ ...l, centers: (res.data?.centers || []).filter((c) => isEmail(c.Email)).map(toCenter) }));
      } else {
        const res = await axios.get(`${API}/api/admin/students/all-students?${SEG[key].query}`, auth());
        setLists((l) => ({ ...l, [key]: (res.data?.students || []).filter((s) => isEmail(s.email)).map(toStudent) }));
      }
    } catch {
      setLists((l) => ({ ...l, [key]: [] }));
    }
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/students/recipient-groups`, auth());
      setGroups(res.data?.groups || res.data?.data || []);
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => {
    if (view !== 'new') return;
    ['demo-done', 'demo-missed', 'active', 'former', 'centers'].forEach((k) => {
      if (lists[k] === undefined) loadSegment(k);
    });
    if (groups === null) loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const startNew = () => {
    setStep(1);
    setSegment('');
    setPicked(new Set());
    setDraft(EMPTY_DRAFT);
    setProgress(null);
    setSearch('');
    setGroupId(null);
    go('new');
  };

  const people = segment === 'saved' ? lists[`group-${groupId}`] || [] : lists[segment] || [];
  const isCenterAudience = segment === 'centers';
  const recipients = useMemo(() => people.filter((p) => picked.has(p.id)), [people, picked]);

  const chooseSegment = (key) => {
    setSegment(key);
    setSearch('');
    if (key !== 'saved') {
      const list = lists[key] || [];
      setPicked(new Set(list.map((p) => p.id)));
    } else {
      setPicked(new Set());
      setGroupId(null);
    }
    // suggest the matching template if the message is still empty
    if (!draft.subject && !draft.message.replace('Dear parent,', '').trim()) {
      const match = Object.entries(emailTemplates).find(([, t]) => t.for.includes(key));
      if (match) applyTemplate(match[0], true);
    }
  };

  // when a list finishes loading for the open segment, select everyone
  useEffect(() => {
    if (segment && segment !== 'saved' && Array.isArray(lists[segment]) && picked.size === 0) {
      setPicked(new Set(lists[segment].map((p) => p.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lists, segment]);

  const chooseGroup = async (g) => {
    setGroupId(g.Id);
    const key = `group-${g.Id}`;
    setLists((l) => ({ ...l, [key]: null }));
    try {
      const res = await axios.get(`${API}/api/admin/students/recipient-groups/${g.Id}`, auth());
      const list = (res.data?.recipients || [])
        .filter((r) => isEmail(r.Email))
        .map((r) => toStudent({ id: r.StudentId || r.Email, studentName: r.StudentName, email: r.Email, lastClassDate: r.LastClassDate }));
      setLists((l) => ({ ...l, [key]: list }));
      setPicked(new Set(list.map((p) => p.id)));
      axios.put(`${API}/api/admin/students/recipient-groups/${g.Id}/use`, {}, auth()).catch(() => {});
    } catch {
      setLists((l) => ({ ...l, [key]: [] }));
      say("Couldn't open this list.", 'bad');
    }
  };

  const q = search.trim().toLowerCase();
  const shown = people.filter((p) => !q || p.name.toLowerCase().includes(q) || String(p.email).toLowerCase().includes(q));
  const togglePerson = (id) =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const pickAll = (on) =>
    setPicked((s) => {
      const n = new Set(s);
      shown.forEach((p) => (on ? n.add(p.id) : n.delete(p.id)));
      return n;
    });

  const doSaveList = async () => {
    if (!saveList?.name?.trim()) return setSaveList((s) => ({ ...s, error: 'Please give the list a name.' }));
    setSaveList((s) => ({ ...s, saving: true, error: '' }));
    try {
      await axios.post(
        `${API}/api/admin/students/save-recipient-group`,
        {
          groupName: saveList.name.trim(),
          description: SEG[segment]?.label || '',
          recipients: recipients.map((r) => ({ id: r.rawId, email: r.email, studentName: r.name, lastClassDate: r.lastClassDate })),
        },
        { ...auth({ 'X-User-Id': userData?.id || 'admin' }), timeout: 120000 }
      );
      setSaveList(null);
      say('List saved');
      loadGroups();
    } catch (err) {
      setSaveList((s) => ({ ...s, saving: false, error: err.response?.data?.error || "Couldn't save the list." }));
    }
    return undefined;
  };

  /* ---------- writing ---------- */
  function applyTemplate(key, silent) {
    const t = emailTemplates[key];
    if (!t) return;
    const doIt = () =>
      setDraft({
        templateKey: key,
        subject: t.subject,
        preheader: t.preheader,
        heading: t.heading,
        message: t.message,
        buttonType: t.buttonType,
        buttonLabel: t.buttonLabel,
        buttonUrl: '',
      });
    const touched = draft.templateKey ? draft.message !== emailTemplates[draft.templateKey]?.message : draft.message.trim();
    if (!silent && touched) {
      setConfirm({ title: `Use “${t.name}”?`, text: 'Your current text will be replaced.', okLabel: 'Use template', onOk: doIt });
    } else doIt();
  }

  const setField = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  const insertTag = (tag) => {
    const field = lastField.current;
    const el = document.getElementById(`rmase-f-${field}`);
    const value = draft[field] || '';
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    setDraft((d) => ({ ...d, [field]: value.slice(0, start) + tag + value.slice(end) }));
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const baseHtml = useMemo(() => renderEmail({ ...draft, audience: segment }), [draft, segment]);
  const sample = recipients[0] || people[0] || (isCenterAudience ? { kind: 'center', name: 'Your center' } : { kind: 'student', name: 'Karim Haddad' });
  const previewHtml = useMemo(() => fillTags(baseHtml, valuesFor(sample)), [baseHtml, sample]);
  const previewSubject = fillTags(draft.subject, valuesFor(sample), { html: false });

  const leftovers = leftoverPlaceholders(draft.subject, draft.preheader, draft.heading, draft.message, draft.buttonLabel);
  const wrongTags = !isCenterAudience
    ? /\{\{centerName\}\}/.test(draft.subject + draft.message + draft.heading)
    : /\{\{(firstName|studentName|lastClassDate)\}\}/.test(draft.subject + draft.message + draft.heading);
  const problems = [
    !draft.subject.trim() && 'Add a subject.',
    !draft.message.trim() && 'Write the message.',
    draft.buttonType !== 'none' && !draft.buttonLabel.trim() && 'Write the button text (or choose “No button”).',
    draft.buttonType === 'custom' && !/^https?:\/\//.test(draft.buttonUrl) && 'The button link must start with https://',
    leftovers.length > 0 && `Fill in: ${leftovers.join(', ')}`,
    wrongTags && (isCenterAudience ? 'Centers have no child name: remove the child tags.' : 'Parents have no center name: remove {{centerName}}.'),
  ].filter(Boolean);

  const canGo = (n) => (n === 1 ? true : n === 2 ? recipients.length > 0 : recipients.length > 0 && problems.length === 0);

  /* ---------- test + send ---------- */
  const personalise = (r, html) => {
    const v = valuesFor(r);
    const base = { email: r.email, personalizedSubject: fillTags(draft.subject, v, { html: false }), personalizedBody: fillTags(html, v) };
    return r.kind === 'center' ? { ...base, centerName: r.name } : { ...base, studentName: r.name, lastClassDate: r.lastClassDate };
  };

  const sendTest = async () => {
    if (!isEmail(testEmail)) return say('Enter your email address for the test.', 'bad');
    try {
      localStorage.setItem(TEST_KEY, testEmail.trim());
    } catch {
      /* private mode */
    }
    setTesting(true);
    try {
      const one = personalise({ ...sample, email: testEmail.trim() }, baseHtml);
      one.personalizedSubject = `[Test] ${one.personalizedSubject}`;
      const endpoint = isCenterAudience ? '/api/admin/students/centers/send-broadcast' : '/api/admin/students/send-broadcast-email';
      const res = await axios.post(
        `${API}${endpoint}`,
        { recipients: [one], subject: `[Test] ${draft.subject}`, body: baseHtml, type: 'test' },
        { ...auth({ 'X-User-Id': userData?.id || 'admin' }), timeout: 60000 }
      );
      if (res.data?.totalSent) say(`Test sent to ${testEmail.trim()}. Check your inbox.`);
      else say("The test email couldn't be sent.", 'bad');
    } catch (err) {
      say(err.response?.data?.error || "The test email couldn't be sent.", 'bad');
    } finally {
      setTesting(false);
    }
    return undefined;
  };

  const sendAll = async () => {
    const list = recipients.map((r) => personalise(r, baseHtml));
    const endpoint = isCenterAudience ? '/api/admin/students/centers/send-broadcast' : '/api/admin/students/send-broadcast-email';
    const type = isCenterAudience ? 'centers-broadcast' : 'broadcast';
    let logId = null;
    let sent = 0;
    let failed = 0;
    setProgress({ total: list.length, done: 0, sent: 0, failed: 0, finished: false });
    const batches = packBatches(list, { subject: draft.subject, body: baseHtml, type, logId: 999999 });
    let done = 0;
    for (let b = 0; b < batches.length; b += 1) {
      const chunk = batches[b];
      const last = b === batches.length - 1;
      try {
        const res = await axios.post(
          `${API}${endpoint}`,
          { recipients: chunk, subject: draft.subject, body: baseHtml, type, logId },
          { ...auth({ 'X-User-Id': userData?.id || 'admin' }), timeout: 90000 }
        );
        logId = res.data?.logId || logId;
        sent += res.data?.totalSent || 0;
        failed += res.data?.totalFailed || 0;
        done += chunk.length;
        setProgress({ total: list.length, done, sent, failed, finished: false });
        if (!last) await wait(CHUNK_DELAY);
      } catch (err) {
        failed += chunk.length;
        done += chunk.length;
        console.error('Email batch failed:', err?.response?.status, err?.response?.data?.error || err?.message);
        setProgress({ total: list.length, done, sent, failed, finished: false });
        if (!last) await wait(ERROR_DELAY);
      }
    }
    setProgress({ total: list.length, done: list.length, sent, failed, finished: true });
    setLogs([]);
  };

  const askSend = () => {
    if (!canGo(3)) return;
    const batches = Math.ceil(recipients.length / CHUNK_SIZE);
    const mins = Math.max(1, Math.ceil(((batches - 1) * CHUNK_DELAY + recipients.length * 1000) / 60000));
    setConfirm({
      title: `Send to ${plural(recipients.length, isCenterAudience ? 'center' : 'parent', isCenterAudience ? 'centers' : 'parents')}?`,
      text: `“${draft.subject}”. It takes about ${plural(mins, 'minute', 'minutes')}. Keep this page open until it finishes.`,
      okLabel: 'Send now',
      onOk: sendAll,
    });
  };

  // retry the people who failed in a report
  const retryFailed = (rep) => {
    const failedPeople = (rep.recipients || []).filter((r) => r.Status === 'Failed');
    if (!failedPeople.length || !rep.log?.Body) return;
    const isCenter = rep.campaign.type === 'centers-broadcast';
    setConfirm({
      title: `Send again to ${plural(failedPeople.length, 'person', 'people')}?`,
      text: 'Only the emails that failed will be sent again.',
      okLabel: 'Send again',
      onOk: async () => {
        const endpoint = isCenter ? '/api/admin/students/centers/send-broadcast' : '/api/admin/students/send-broadcast-email';
        const list = failedPeople.map((r) => {
          const p = isCenter ? { kind: 'center', name: r.StudentName } : { kind: 'student', name: r.StudentName };
          const v = valuesFor(p);
          return {
            email: r.Email,
            ...(isCenter ? { centerName: r.StudentName } : { studentName: r.StudentName }),
            personalizedSubject: fillTags(rep.log.Subject, v, { html: false }),
            personalizedBody: fillTags(rep.log.Body, v),
          };
        });
        let logId = null;
        let ok = 0;
        const batches = packBatches(list, { subject: rep.log.Subject, body: rep.log.Body, type: rep.campaign.type, logId: 999999 });
        for (let b = 0; b < batches.length; b += 1) {
          try {
            const res = await axios.post(
              `${API}${endpoint}`,
              { recipients: batches[b], subject: rep.log.Subject, body: rep.log.Body, type: rep.campaign.type, logId },
              { ...auth({ 'X-User-Id': userData?.id || 'admin' }), timeout: 90000 }
            );
            logId = res.data?.logId || logId;
            ok += res.data?.totalSent || 0;
            if (b < batches.length - 1) await wait(CHUNK_DELAY);
          } catch {
            /* counted as failed */
          }
        }
        say(`${ok} of ${list.length} sent again`, ok === list.length ? 'good' : 'bad');
        setReport(null);
        loadLogs();
      },
    });
  };

  /* ================= render ================= */
  const segCount = (key) => {
    if (key === 'saved') return groups ? plural(groups.length, 'list', 'lists') : '…';
    const l = lists[key];
    return l === undefined || l === null ? '…' : plural(l.length, key === 'centers' ? 'center' : 'parent', key === 'centers' ? 'centers' : 'parents');
  };

  return (
    <div className="rmase">
      {/* ---------- header ---------- */}
      <header className="rmase-header">
        <div>
          <h1>{view === 'new' ? 'New email' : 'Emails'}</h1>
          <p>{view === 'new' ? 'Choose who gets it, write it, test it, send it.' : 'Professional emails to parents and centers, with a report for each one.'}</p>
        </div>
        {view === 'new' ? (
          <button type="button" className="rmase-btn rmase-btn--ghost" onClick={() => go('sent')} disabled={progress && !progress.finished}>
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
        ) : (
          <button type="button" className="rmase-btn" onClick={startNew}>
            <FontAwesomeIcon icon={faPlus} /> New email
          </button>
        )}
      </header>

      {view !== 'new' && (
        <div className="rmase-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={view === 'sent'} className={view === 'sent' ? 'is-on' : ''} onClick={() => go('sent')}>
            Sent
          </button>
          <button type="button" role="tab" aria-selected={view === 'centers'} className={view === 'centers' ? 'is-on' : ''} onClick={() => go('centers')}>
            Centers
          </button>
        </div>
      )}

      {/* ================= Sent ================= */}
      {view === 'sent' && (
        <>
          <section className="rmase-kpis">
            <div className="rmase-kpi">
              <span>Emails this month</span>
              <strong>{monthStats.count}</strong>
              <small>campaigns sent</small>
            </div>
            <div className="rmase-kpi">
              <span>Delivered</span>
              <strong>{monthStats.sent}</strong>
              <small>{monthStats.rate === null ? 'nothing sent yet' : `${monthStats.rate}% sent successfully`}</small>
            </div>
            <div className={`rmase-kpi ${monthStats.failed ? 'is-bad' : ''}`}>
              <span>Failed</span>
              <strong>{monthStats.failed}</strong>
              <small>open a report to send again</small>
            </div>
          </section>

          {logsError && <p className="rmase-error">{logsError}</p>}
          {logsLoading && !logs.length ? (
            <div className="rmase-skel" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          ) : campaigns.length === 0 ? (
            <div className="rmase-empty">
              <FontAwesomeIcon icon={faEnvelopeOpenText} />
              <strong>No emails sent yet</strong>
              <p>Your first email is a few clicks away.</p>
              <button type="button" className="rmase-btn" onClick={startNew}>
                <FontAwesomeIcon icon={faPlus} /> New email
              </button>
            </div>
          ) : (
            <ul className="rmase-campaigns">
              {campaigns.map((c) => {
                const pct = c.total ? Math.round((c.sent / c.total) * 100) : 0;
                return (
                  <li key={c.key}>
                    <button type="button" className="rmase-campaign" onClick={() => openReport(c)}>
                      <span className="rmase-campaign-main">
                        <strong>{readable(c.subject)}</strong>
                        <small>
                          {sentAt(c.at)} · {c.type === 'centers-broadcast' ? 'Centers' : 'Parents'}
                        </small>
                      </span>
                      <span className="rmase-campaign-stats">
                        <span className="rmase-meter" aria-hidden="true">
                          <i style={{ width: `${pct}%` }} />
                        </span>
                        <small>
                          {c.sent} of {c.total} sent
                          {c.failed ? <b className="rmase-failed"> · {c.failed} failed</b> : null}
                        </small>
                      </span>
                      <span className={`rmase-pill ${c.failed ? (c.sent ? 'is-warn' : 'is-bad') : 'is-good'}`}>
                        {c.failed ? (c.sent ? 'Partly sent' : 'Failed') : 'Sent'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      {/* ================= Centers ================= */}
      {view === 'centers' && (
        <section className="rmase-card">
          <div className="rmase-card-head">
            <h2>Centers & schools</h2>
            <button type="button" className="rmase-btn rmase-btn--ghost" onClick={() => openCenter(null)}>
              <FontAwesomeIcon icon={faPlus} /> Add center
            </button>
          </div>
          {centers === null ? (
            <div className="rmase-skel" aria-hidden="true">
              <span />
            </div>
          ) : centers.length === 0 ? (
            <p className="rmase-muted">No centers yet.</p>
          ) : (
            <ul className="rmase-rows">
              {centers.map((c) => (
                <li key={c.Id}>
                  <span className="rmase-row-main">
                    <strong>{c.CenterName}</strong>
                    <small>
                      {c.Email}
                      {c.Mobile ? ` · ${c.Mobile}` : ''}
                      {c.Address ? ` · ${c.Address}` : ''}
                    </small>
                  </span>
                  <span className="rmase-row-actions">
                    <button type="button" className="rmase-icon-btn" onClick={() => openCenter(c)} aria-label={`Edit ${c.CenterName}`}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button type="button" className="rmase-icon-btn is-danger" onClick={() => deleteCenter(c)} aria-label={`Delete ${c.CenterName}`}>
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* ================= New email ================= */}
      {view === 'new' && !progress && (
        <>
          <Steps step={step} setStep={setStep} canGo={canGo} />

          {/* ----- 1. audience ----- */}
          {step === 1 && (
            <div className="rmase-audience">
              <div className="rmase-segments" role="radiogroup" aria-label="Who gets this email">
                {SEGMENTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    role="radio"
                    aria-checked={segment === s.key}
                    className={`rmase-segment ${segment === s.key ? 'is-on' : ''}`}
                    onClick={() => chooseSegment(s.key)}
                  >
                    <span className="rmase-segment-icon">
                      <FontAwesomeIcon icon={s.icon} />
                    </span>
                    <span className="rmase-segment-text">
                      <strong>{s.label}</strong>
                      <small>{s.desc}</small>
                    </span>
                    <em>{segCount(s.key)}</em>
                  </button>
                ))}
              </div>

              {segment === 'saved' && (
                <section className="rmase-card">
                  <div className="rmase-card-head">
                    <h2>Your saved lists</h2>
                  </div>
                  {groups === null ? (
                    <p className="rmase-muted">Loading…</p>
                  ) : groups.length === 0 ? (
                    <p className="rmase-muted">No saved lists yet. Pick an audience, then “Save as list”.</p>
                  ) : (
                    <div className="rmase-chips">
                      {groups.map((g) => (
                        <button key={g.Id} type="button" className={`rmase-chip ${groupId === g.Id ? 'is-on' : ''}`} onClick={() => chooseGroup(g)}>
                          {g.GroupName} <small>{g.RecipientCount}</small>
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {segment && (segment !== 'saved' || groupId) && (
                <section className="rmase-card">
                  <div className="rmase-card-head">
                    <h2>
                      {recipients.length} of {people.length} selected
                    </h2>
                    <div className="rmase-head-actions">
                      <button type="button" className="rmase-link" onClick={() => pickAll(true)}>
                        Select all
                      </button>
                      <button type="button" className="rmase-link" onClick={() => pickAll(false)}>
                        Clear
                      </button>
                      {!isCenterAudience && segment !== 'saved' && recipients.length > 0 && (
                        <button type="button" className="rmase-link" onClick={() => setSaveList({ name: '', saving: false, error: '' })}>
                          <FontAwesomeIcon icon={faFloppyDisk} /> Save as list
                        </button>
                      )}
                    </div>
                  </div>

                  {saveList && (
                    <div className="rmase-inline-form">
                      <input
                        type="text"
                        placeholder="List name, e.g. Python parents"
                        value={saveList.name}
                        onChange={(e) => setSaveList((s) => ({ ...s, name: e.target.value }))}
                        aria-label="List name"
                      />
                      <button type="button" className="rmase-btn" onClick={doSaveList} disabled={saveList.saving}>
                        {saveList.saving ? 'Saving…' : 'Save'}
                      </button>
                      <button type="button" className="rmase-icon-btn" onClick={() => setSaveList(null)} aria-label="Cancel">
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                      {saveList.error && <p className="rmase-error">{saveList.error}</p>}
                    </div>
                  )}

                  <label className="rmase-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input type="search" placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} />
                  </label>

                  {lists[segment === 'saved' ? `group-${groupId}` : segment] === null ? (
                    <p className="rmase-muted">Loading…</p>
                  ) : shown.length === 0 ? (
                    <p className="rmase-muted">{people.length ? 'No match.' : 'Nobody in this group yet.'}</p>
                  ) : (
                    <ul className="rmase-people">
                      {shown.map((p) => (
                        <li key={p.id}>
                          <label className={picked.has(p.id) ? 'is-on' : ''}>
                            <input type="checkbox" checked={picked.has(p.id)} onChange={() => togglePerson(p.id)} />
                            <span className="rmase-person">
                              <strong>{p.name}</strong>
                              <small>
                                {p.email}
                                {p.lastClassDate ? ` · last class ${classDate(p.lastClassDate)}` : ''}
                              </small>
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}

              <div className="rmase-footer">
                <span className="rmase-muted">{segment ? plural(recipients.length, 'person', 'people') + ' will get this email' : 'Choose who gets this email'}</span>
                <button type="button" className="rmase-btn" disabled={!canGo(2)} onClick={() => setStep(2)}>
                  Next: write <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </div>
          )}

          {/* ----- 2. write ----- */}
          {step === 2 && (
            <div className="rmase-write">
              <div className="rmase-editor">
                <section className="rmase-card">
                  <div className="rmase-card-head">
                    <h2>Start from a template</h2>
                  </div>
                  <div className="rmase-templates">
                    {Object.entries(emailTemplates)
                      .filter(([, t]) => (isCenterAudience ? t.for.includes('centers') || !t.for.length : !t.for.includes('centers')))
                      .map(([key, t]) => (
                        <button key={key} type="button" className={`rmase-template ${draft.templateKey === key ? 'is-on' : ''}`} onClick={() => applyTemplate(key)}>
                          <strong>
                            {t.name}
                            {t.for.includes(segment) && <em>Suggested</em>}
                          </strong>
                          <small>{t.hint}</small>
                        </button>
                      ))}
                  </div>
                </section>

                <section className="rmase-card">
                  <label className="rmase-field">
                    <span>
                      Subject <small>{draft.subject.length}/60 recommended</small>
                    </span>
                    <input id="rmase-f-subject" type="text" value={draft.subject} onChange={setField('subject')} onFocus={() => (lastField.current = 'subject')} maxLength={150} />
                  </label>
                  <label className="rmase-field">
                    <span>
                      Preview text <small>shown after the subject in the inbox</small>
                    </span>
                    <input id="rmase-f-preheader" type="text" value={draft.preheader} onChange={setField('preheader')} onFocus={() => (lastField.current = 'preheader')} maxLength={140} />
                  </label>
                  <label className="rmase-field">
                    <span>Title</span>
                    <input id="rmase-f-heading" type="text" value={draft.heading} onChange={setField('heading')} onFocus={() => (lastField.current = 'heading')} maxLength={120} />
                  </label>
                  <label className="rmase-field">
                    <span>
                      Message <small>empty line = new paragraph · “- ” = bullet · **bold**</small>
                    </span>
                    <textarea
                      id="rmase-f-message"
                      ref={messageRef}
                      rows={12}
                      dir="auto"
                      value={draft.message}
                      onChange={setField('message')}
                      onFocus={() => (lastField.current = 'message')}
                    />
                  </label>
                  <div className="rmase-tags">
                    <span>Insert:</span>
                    {TAGS[isCenterAudience ? 'center' : 'student'].map(([tag, label]) => (
                      <button key={tag} type="button" className="rmase-chip" onClick={() => insertTag(tag)}>
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="rmase-button-row">
                    <label className="rmase-field">
                      <span>Button goes to</span>
                      <select value={draft.buttonType} onChange={setField('buttonType')}>
                        {Object.entries(BUTTONS).map(([k, b]) => (
                          <option key={k} value={k}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {draft.buttonType !== 'none' && (
                      <label className="rmase-field">
                        <span>Button text</span>
                        <input id="rmase-f-buttonLabel" type="text" value={draft.buttonLabel} onChange={setField('buttonLabel')} onFocus={() => (lastField.current = 'buttonLabel')} maxLength={40} />
                      </label>
                    )}
                    {draft.buttonType === 'custom' && (
                      <label className="rmase-field rmase-field--wide">
                        <span>Link</span>
                        <input type="url" placeholder="https://" value={draft.buttonUrl} onChange={setField('buttonUrl')} />
                      </label>
                    )}
                  </div>
                </section>

                {problems.length > 0 && (
                  <ul className="rmase-problems">
                    {problems.map((p) => (
                      <li key={p}>
                        <FontAwesomeIcon icon={faTriangleExclamation} /> {p}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="rmase-footer">
                  <button type="button" className="rmase-btn rmase-btn--ghost" onClick={() => setStep(1)}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                  </button>
                  <button type="button" className="rmase-btn rmase-btn--ghost rmase-only-phone" onClick={() => setShowPreview(true)}>
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </button>
                  <button type="button" className="rmase-btn" disabled={!canGo(3)} onClick={() => setStep(3)}>
                    Next: test & send <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              </div>

              <aside className="rmase-side">
                <Preview html={previewHtml} device={device} setDevice={setDevice} subject={previewSubject} />
                <p className="rmase-muted rmase-center">Preview for {sample.name}</p>
              </aside>
            </div>
          )}

          {/* ----- 3. test & send ----- */}
          {step === 3 && (
            <div className="rmase-write">
              <div className="rmase-editor">
                <section className="rmase-card rmase-summary">
                  <h2>Ready to send</h2>
                  <dl>
                    <div>
                      <dt>To</dt>
                      <dd>
                        {plural(recipients.length, isCenterAudience ? 'center' : 'parent', isCenterAudience ? 'centers' : 'parents')}
                        <small>{SEG[segment]?.label}</small>
                      </dd>
                    </div>
                    <div>
                      <dt>From</dt>
                      <dd>RoboMind Academy</dd>
                    </div>
                    <div>
                      <dt>Subject</dt>
                      <dd>{previewSubject}</dd>
                    </div>
                    <div>
                      <dt>Time</dt>
                      <dd>about {plural(Math.max(1, Math.ceil(((Math.ceil(recipients.length / CHUNK_SIZE) - 1) * CHUNK_DELAY + recipients.length * 1000) / 60000)), 'minute', 'minutes')}</dd>
                    </div>
                  </dl>
                </section>

                <section className="rmase-card">
                  <div className="rmase-card-head">
                    <h2>
                      <FontAwesomeIcon icon={faFlask} /> Send yourself a test first
                    </h2>
                  </div>
                  <p className="rmase-muted">See it in your own inbox (Gmail, phone…) before parents do.</p>
                  <div className="rmase-inline-form">
                    <input type="email" placeholder="your@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} aria-label="Your email" />
                    <button type="button" className="rmase-btn rmase-btn--ghost" onClick={sendTest} disabled={testing}>
                      {testing ? 'Sending…' : 'Send test'}
                    </button>
                  </div>
                </section>

                <div className="rmase-footer">
                  <button type="button" className="rmase-btn rmase-btn--ghost" onClick={() => setStep(2)}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Edit
                  </button>
                  <button type="button" className="rmase-btn rmase-btn--ghost rmase-only-phone" onClick={() => setShowPreview(true)}>
                    <FontAwesomeIcon icon={faEye} /> Preview
                  </button>
                  <button type="button" className="rmase-btn rmase-btn--send" onClick={askSend} disabled={!canGo(3)}>
                    <FontAwesomeIcon icon={faPaperPlane} /> Send to {recipients.length}
                  </button>
                </div>
              </div>
              <aside className="rmase-side">
                <Preview html={previewHtml} device={device} setDevice={setDevice} subject={previewSubject} />
              </aside>
            </div>
          )}
        </>
      )}

      {/* ----- sending ----- */}
      {view === 'new' && progress && (
        <section className="rmase-card rmase-progress" aria-live="polite">
          <span className={`rmase-progress-icon ${progress.finished ? (progress.failed ? 'is-warn' : 'is-good') : ''}`}>
            <FontAwesomeIcon icon={progress.finished ? (progress.failed ? faTriangleExclamation : faCircleCheck) : faPaperPlane} />
          </span>
          <h2>
            {progress.finished
              ? progress.failed
                ? `${progress.sent} sent, ${progress.failed} failed`
                : `Sent to ${plural(progress.sent, 'person', 'people')}`
              : `Sending… ${progress.done} of ${progress.total}`}
          </h2>
          <span className="rmase-meter is-big" aria-hidden="true">
            <i style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
          </span>
          <p className="rmase-muted">
            {progress.finished
              ? progress.failed
                ? 'Open the report to see who didn’t get it and send again.'
                : 'Everything went out. The report is in Sent.'
              : 'Keep this page open until it finishes.'}
          </p>
          {progress.finished && (
            <div className="rmase-footer is-center">
              <button type="button" className="rmase-btn rmase-btn--ghost" onClick={startNew}>
                <FontAwesomeIcon icon={faPlus} /> Another email
              </button>
              <button type="button" className="rmase-btn" onClick={() => go('sent')}>
                See reports
              </button>
            </div>
          )}
        </section>
      )}

      {/* ----- phone preview ----- */}
      {showPreview && view === 'new' && (
        <div className="rmase-modal" role="dialog" aria-modal="true" aria-label="Email preview">
          <div className="rmase-modal-card is-preview">
            <Preview html={previewHtml} device="mobile" setDevice={() => {}} subject={previewSubject} onClose={() => setShowPreview(false)} />
          </div>
        </div>
      )}

      {/* ----- report ----- */}
      {report && (
        <div className="rmase-modal" role="dialog" aria-modal="true" aria-label="Email report" onClick={(e) => e.target === e.currentTarget && setReport(null)}>
          <div className="rmase-modal-card is-wide">
            <div className="rmase-card-head">
              <h2>{readable(report.campaign.subject)}</h2>
              <button type="button" className="rmase-icon-btn" onClick={() => setReport(null)} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <p className="rmase-muted">
              {sentAt(report.campaign.at)} · {report.campaign.sent} of {report.campaign.total} sent
              {report.campaign.failed ? ` · ${report.campaign.failed} failed` : ''}
            </p>
            {report.loading ? (
              <p className="rmase-muted">Loading…</p>
            ) : report.error ? (
              <p className="rmase-error">{report.error}</p>
            ) : (
              <div className="rmase-report">
                <div className="rmase-report-list">
                  {(report.recipients || []).some((r) => r.Status === 'Failed') && (
                    <button type="button" className="rmase-btn rmase-btn--ghost" onClick={() => retryFailed(report)}>
                      <FontAwesomeIcon icon={faRotateRight} /> Send again to the failed ones
                    </button>
                  )}
                  <ul className="rmase-rows">
                    {(report.recipients || []).map((r, i) => (
                      <li key={`${r.Email}-${i}`}>
                        <span className="rmase-row-main">
                          <strong>{r.StudentName || r.Email}</strong>
                          <small>
                            {r.Email}
                            {r.ErrorMessage ? ` · ${r.ErrorMessage}` : ''}
                          </small>
                        </span>
                        <span className={`rmase-pill ${r.Status === 'Failed' ? 'is-bad' : 'is-good'}`}>{r.Status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                {report.log?.Body && (
                  <div className="rmase-report-preview">
                    <div className="rmase-frame is-mobile">
                      <iframe title="Email that was sent" srcDoc={fillTags(report.log.Body, { firstName: 'Karim', studentName: 'Karim Haddad', centerName: 'Your center' })} sandbox="" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ----- center form ----- */}
      {centerModal && (
        <div className="rmase-modal" role="dialog" aria-modal="true" aria-label={centerModal.mode === 'add' ? 'Add center' : 'Edit center'}>
          <form className="rmase-modal-card" onSubmit={saveCenter}>
            <div className="rmase-card-head">
              <h2>{centerModal.mode === 'add' ? 'Add a center' : 'Edit center'}</h2>
              <button type="button" className="rmase-icon-btn" onClick={() => setCenterModal(null)} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {[
              ['centerName', 'Name', 'text'],
              ['email', 'Email', 'email'],
              ['mobile', 'Phone', 'tel'],
              ['address', 'Address', 'text'],
            ].map(([k, label, type]) => (
              <label key={k} className="rmase-field">
                <span>{label}</span>
                <input type={type} value={centerForm[k]} onChange={(e) => setCenterForm((f) => ({ ...f, [k]: e.target.value }))} />
              </label>
            ))}
            {centerError && <p className="rmase-error">{centerError}</p>}
            <div className="rmase-footer">
              <button type="button" className="rmase-btn rmase-btn--ghost" onClick={() => setCenterModal(null)}>
                Cancel
              </button>
              <button type="submit" className="rmase-btn" disabled={centerSaving}>
                {centerSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ----- confirm ----- */}
      {confirm && (
        <div className="rmase-modal" role="alertdialog" aria-modal="true" aria-label={confirm.title}>
          <div className="rmase-modal-card is-small">
            <h2>{confirm.title}</h2>
            <p className="rmase-muted">{confirm.text}</p>
            <div className="rmase-footer">
              <button type="button" className="rmase-btn rmase-btn--ghost" onClick={() => setConfirm(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={`rmase-btn ${confirm.danger ? 'rmase-btn--danger' : ''}`}
                onClick={() => {
                  const fn = confirm.onOk;
                  setConfirm(null);
                  fn();
                }}
              >
                {confirm.okLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div key={toast.id} className={`rmase-toast is-${toast.tone}`} role="status">
          {toast.text}
        </div>
      )}
    </div>
  );
};

export default SendEmails;
